import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Auth, useAuth } from "../auth/AuthContext";
import { issueTextOrDefault } from "../DAO";
import { DocumentTitle } from "../DocumentTitle";
import { InstanceStore, useInstanceStore } from "./InstanceContext";
import { InstanceForm, InstanceFormData } from "./InstanceForm";

const storageKey = "instance-show-ids";
function readShowIds(): boolean {
   try {
      return localStorage.getItem(storageKey) === "true";
   }
   catch (e) {
      return false;
   }
}
function writeShowIds(show: boolean): void {
   try {
      return localStorage.setItem(storageKey, show + "");
   }
   catch (e) { /* ignore */ }
}



export const InstanceList = (): JSX.Element => {
   const auth: Auth = useAuth();
   if (!auth?.user?.permissions?.instance?.canView) {
      return <div>You do not have permission to be here.</div>;
   }

   const canEdit = useMemo<boolean>(() => !!auth?.user?.permissions?.instance?.canEdit, [auth]);
   const [showIds, setShowIds] = useState<boolean>(readShowIds());
   useEffect(() => {
      // Write "Show Ids" preference to local storage when it changes
      writeShowIds(showIds);
   }, [showIds]);

   const colspan: number = useMemo(() => {
      let cols = 4;
      if (showIds) {
         cols += 2;
      }
      if (canEdit) {
         cols += 1;
      }
      return cols;
   }, [canEdit, showIds]);

   const instanceStore: InstanceStore = useInstanceStore();

   const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);

   function onCancelEdit(): void {
      setSelectedId(null);
   }

   function onSaveEdit(): void {
      setSelectedId(null);
      instanceStore.loadInstances();
   }

   useEffect(() => {
      if (instanceStore.isStale) {
         instanceStore.loadInstances();
      }
   }, [instanceStore.isStale]);

   function renderEditForm(i: InstanceFormData): JSX.Element {
      return <InstanceForm instance={i} onCancel={onCancelEdit} onSave={onSaveEdit} />;
   }

   function renderAddArea(): JSX.Element {
      if (instanceStore.instances !== null && canEdit) { // No longer loading
         if (selectedId === 'new') {
            return renderEditForm({ name: "" });
         }
         if (selectedId === null) {
            return <button id={htmlId("new")} type="button"
               onClick={() => setSelectedId('new')}>New</button>;
         }
      }
      return <></>; // Loading, render nothing
   }

   const htmlId = (suffix?: string): string => {
      const prefix = "instance-list";
      return suffix ? prefix + "_" + suffix : prefix;
   }
   return (<>
      <DocumentTitle title="Instances"></DocumentTitle>
      <article id={htmlId()}>
         {instanceStore.instances && <>
            <label htmlFor={htmlId("showIdsToggle")}>Show Ids:</label>
            <input id={htmlId("showIdsToggle")} type="checkbox"
               defaultChecked={showIds} onChange={() => setShowIds(!showIds)} />

            <table border={1}>
               <thead>
                  <tr>
                     {showIds && <>
                        <th>Id</th>
                        <th>External Id</th>
                     </>}
                     <th>Name</th>
                     <th className="number"># Active Licences</th>
                     <th className="date">Licence Sync Date</th>
                     <th className="date">Report Submit Date</th>
                     {canEdit && <th>Actions</th>}
                  </tr>
               </thead>
               <tbody>
                  {instanceStore.instances?.length && instanceStore.instances.map(i =>
                     <React.Fragment key={i.id}>
                        <tr id={htmlId(`row-${i.id}`)} className={selectedId === i.id ? "selected" : ""}>
                           {showIds && <>
                              <td id={htmlId(`id-${i.id}`)}>{i.id}</td>
                              <td id={htmlId(`ext-id-${i.id}`)}>{i.externalId}</td>
                           </>}
                           <td id={htmlId(`name-${i.id}`)}>{i.name}</td>
                           <td id={htmlId(`lic-count-${i.id}`)} className="number">
                              <Link to={`/licences?instance=${i.id}`} className="link">
                                 {i.activeLicenceCount}
                              </Link>
                           </td>
                           <td id={htmlId(`sync-date-${i.id}`)} className="date">{i.lastLicenceSyncDate}</td>
                           <td id={htmlId(`submit-date-${i.id}`)} className="date">{i.lastReportSubmitDate}</td>
                           {canEdit &&
                              <td id={htmlId(`mod-date-${i.id}`)}>
                                 <button id={htmlId(`rename-${i.id}`)}
                                    type="button" className="link"
                                    disabled={selectedId != null}
                                    onClick={() => setSelectedId(i.id)}>rename</button>
                              </td>
                           }
                        </tr>
                        {selectedId === i.id &&
                           <tr>
                              <td colSpan={colspan}>
                                 {renderEditForm(i)}
                              </td>
                           </tr>
                        }
                     </React.Fragment>)
                     || instanceStore.instances?.length === 0 &&
                     <tr id={htmlId("no-results")}>
                        <td colSpan={colspan}>No records found.</td>
                     </tr>
                  }
               </tbody>
            </table>
         </>
            || instanceStore.isLoading && <div id={htmlId("loading")}>Loading...</div>
            || instanceStore.error?.issues?.length && (
               <div id={htmlId("load-errors")}>
                  {instanceStore.error.issues
                     .map(issueTextOrDefault)
                     .map(msg => <div className="inline-error-message">{msg}</div>).join("\n")}
                  <button type="button" className="link" onClick={instanceStore.loadInstances}>try again</button>
               </div>
            )
         }
         {renderAddArea()}
      </article>
   </>);
};